/**
 * The half-finished stock count, kept safe between visits.
 *
 * Counting 104 items in a working kitchen means being interrupted. The draft has
 * to survive the phone locking, the browser being switched away, the tab being
 * closed, and the person coming back an hour later.
 *
 * Why not the cookie this replaces
 * --------------------------------
 * The old version wrote the whole count into a cookie on every keystroke.
 * Cookies are capped at about 4KB, and a full 104-item count needs roughly 13KB
 * once cookie-encoded. Measured against the real item list, the cookie silently
 * stopped accepting entries after the 33rd item: typing into 50 fields left 32
 * in the draft, with nothing on screen to say so. Everything past that was lost
 * the moment the person navigated away - which is exactly the complaint.
 *
 * localStorage holds several megabytes, is not sent on every HTTP request, and
 * has no expiry of its own; all three are what this needs.
 *
 * What is stored
 * --------------
 * The text as typed, keyed by item id.
 *
 * Ids because names are not unique - there is onion on one shelf and onion on
 * another, and keying by name gave them one shared slot, so counting both left
 * the second overwriting the first.
 *
 * The typed text rather than the computed number because "500+250" should come
 * back as "500+250", still correctable. The old version saved both and restored
 * only the formula, which is why a half-typed "1000-200" came back as an empty
 * box with a red error under it.
 *
 * The unit is stored beside it, because "3" means nothing on its own once a
 * person can count in kilograms: restoring 3 kg as 3 g would be a thousand-fold
 * error, silently, in the recovery path of all places.
 */

const VERSION = 'v1';
const PREFIX = 'divash-inventory-draft';

/** One key per person: a shared kitchen phone must not mix two people's counts. */
export function draftKey(username) {
    return `${PREFIX}:${VERSION}:${username || 'anon'}`;
}

function probeStorage() {
    try {
        const probe = `${PREFIX}:probe`;
        window.localStorage.setItem(probe, '1');
        window.localStorage.removeItem(probe);
        return true;
    } catch (error) {
        return false;
    }
}

// Private browsing on iOS makes localStorage throw. Detected once, up front, so
// the person can be warned before they count rather than after they lose it.
export const STORAGE_AVAILABLE = probeStorage();

/** Today in the kitchen's terms, so a draft can say which day it belongs to. */
export function businessDate(now = new Date()) {
    return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/**
 * Read the saved draft.
 *
 * Returns { entries, savedAt, date }. Anything unreadable is treated as no
 * draft: a corrupt draft that threw on load would lock the person out of the
 * page altogether.
 */
export function loadDraft(username) {
    const empty = { entries: {}, units: {}, savedAt: null, date: null };
    if (!STORAGE_AVAILABLE) return empty;
    try {
        const raw = window.localStorage.getItem(draftKey(username));
        if (!raw) return empty;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.entries) return empty;
        return {
            entries: parsed.entries,
            units: parsed.units || {},
            savedAt: parsed.savedAt ? new Date(parsed.savedAt) : null,
            date: parsed.date || null,
        };
    } catch (error) {
        return empty;
    }
}

/**
 * Write the draft. Returns true on success.
 *
 * The result is returned rather than swallowed, because the bug being fixed
 * here is precisely a store that stopped accepting data without telling anyone.
 */
export function saveDraft(username, entries, units) {
    if (!STORAGE_AVAILABLE) return false;
    try {
        window.localStorage.setItem(draftKey(username), JSON.stringify({
            savedAt: new Date().toISOString(),
            date: businessDate(),
            entries,
            units: units || {},
        }));
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Remove the cookie this replaced.
 *
 * It is dead once the localStorage draft is in use, but a leftover 4KB cookie
 * is sent up with every single request to the API for as long as it lives.
 */
export function dropLegacyCookie() {
    try {
        document.cookie = 'divash-inventory-form=; path=/; max-age=0';
    } catch (error) {
        /* nothing to clean up */
    }
}

export function clearDraft(username) {
    if (!STORAGE_AVAILABLE) return;
    try {
        window.localStorage.removeItem(draftKey(username));
    } catch (error) {
        /* stale either way */
    }
}

/** "just now", "12 minutes ago", "3 hours ago", "yesterday". */
export function savedAgo(savedAt, now = new Date()) {
    if (!savedAt) return '';
    const seconds = Math.max(0, Math.round((now - savedAt) / 1000));
    if (seconds < 45) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return days === 1 ? 'yesterday' : `${days} days ago`;
}
